import { useMemo, useRef, useState } from 'react';
import { Button, Labeled, Select, TextInput, Toggle } from '@tes/ui';
import { Copy, Check, Trash2, Plus, Upload } from 'lucide-react';
import { DEFAULT_TEXT_STYLE, INPUT_TYPES } from '@tes/domain';
import { FieldPreview } from '../editor/FieldPreview';
import { FieldBox, PageCanvas } from '../editor/PageCanvas';
import type { EditorField } from '../editor/editorStore';
import { useFormatDate } from '../lib/useSettings';
import { DEFAULT_FIELD_SIZE } from '../lib/quickSend';
import type { InputType, PageSize } from '../lib/types';

/**
 * Field Previewer — a standalone, client-side tool to read off field
 * coordinates for the ad-hoc uploads API (POST /v1/uploads → POST /v1/envelopes).
 * Upload a PDF, drag/resize field boxes on it, and copy a ready-to-send
 * `fields[]` array. Coordinates are top-left origin, in PDF points — the exact
 * system the API normalizes to (matches the template editor and stamp engine).
 * Everything runs in the browser; nothing is uploaded here.
 *
 * Renders the SAME canvas and field-box components as the template editor,
 * over local state instead of the editor store.
 */


/** A previewer field is an editor input field — same shape, so FieldPreview and FieldBox take it as-is. */
type PField = EditorField & {
  kind: 'input';
  type: InputType;
  signerRole: string;
  required: boolean;
};

const round = (n: number) => Math.round(n);

export default function FieldPreviewerPage() {
  const [file, setFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [numPages, setNumPages] = useState(0);
  const [page, setPage] = useState(1);
  const [zoom, setZoom] = useState(1);
  const [pageSizes, setPageSizes] = useState<Record<number, PageSize>>({});
  const [fields, setFields] = useState<PField[]>([]);
  const [selected, setSelected] = useState<string | null>(null);
  const [newType, setNewType] = useState<InputType>('signature');
  const [lastRole, setLastRole] = useState('customer');
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Render date fields with the platform's real display format (e.g. DD/MM/YYYY).
  const { pattern: datePattern } = useFormatDate();

  const size = pageSizes[page];
  const onPage = fields.filter((f) => f.page === page);

  function pickFile(f: File | undefined) {
    if (!f) return;
    if (f.type !== 'application/pdf') {
      setFileError(
        'Please choose a PDF. Images are accepted by the API, but this previewer renders PDFs only.',
      );
      return;
    }
    setFileError(null);
    setFile(f);
    setNumPages(0);
    setPage(1);
    setPageSizes({});
    setFields([]);
    setSelected(null);
  }

  function addField() {
    if (!size) return;
    const s = DEFAULT_FIELD_SIZE[newType];
    // Drop it near the top-left with a 1-inch margin, clamped to the page.
    const x = Math.min(72, Math.max(0, size.width - s.w));
    const y = Math.min(72, Math.max(0, size.height - s.h));
    const uid = crypto.randomUUID();
    const required = newType !== 'date';
    setFields((prev) => [
      ...prev,
      {
        uid,
        kind: 'input',
        page,
        x,
        y,
        w: s.w,
        h: s.h,
        type: newType,
        signerRole: lastRole,
        required,
        ...(newType === 'date' ? { dateMode: 'auto' as const } : {}),
        ...(newType === 'checkbox' ? { mustCheck: required } : {}),
      },
    ]);
    setSelected(uid);
  }

  function update(uid: string, patch: Partial<PField>) {
    setFields((prev) => prev.map((f) => (f.uid === uid ? { ...f, ...patch } : f)));
  }

  function remove(uid: string) {
    setFields((prev) => prev.filter((f) => f.uid !== uid));
    if (selected === uid) setSelected(null);
  }

  // The exact shape to paste into a POST /v1/envelopes ad-hoc document.
  const fieldsJson = useMemo(() => {
    const out = fields
      .slice()
      .sort((a, b) => a.page - b.page || a.y - b.y || a.x - b.x)
      .map((f) => ({
        type: f.type,
        signerRole: f.signerRole,
        required: f.required,
        page: f.page,
        x: round(f.x),
        y: round(f.y),
        w: round(f.w),
        h: round(f.h),
      }));
    return JSON.stringify(out, null, 2);
  }, [fields]);

  async function copyJson() {
    await navigator.clipboard.writeText(fieldsJson);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <h1 className="text-lg font-semibold">Field Previewer</h1>
        {file && (
          <Button variant="outline" size="sm" onClick={() => inputRef.current?.click()}>
            <Upload size={15} className="mr-1" /> Replace PDF
          </Button>
        )}
      </div>
      <p className="mb-5 text-sm text-slate-500">
        Upload a PDF and place fields to read off their <strong>page, x, y, width, height</strong> in
        PDF points (top-left origin) — the exact coordinates <code className="rounded bg-slate-100 px-1">POST /v1/envelopes</code> expects.
        Everything stays in your browser.
      </p>

      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        className="hidden"
        onChange={(e) => pickFile(e.target.files?.[0])}
      />

      {fileError && <p className="mb-3 text-sm text-red-600">{fileError}</p>}

      {!file ? (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          className="flex w-full flex-col items-center gap-2 rounded-xl border-2 border-dashed border-slate-300 p-16 text-slate-400 hover:border-brand-400 hover:text-brand-600"
        >
          <Upload size={28} />
          <span className="text-sm font-medium">Choose a PDF to place fields on</span>
        </button>
      ) : (
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Canvas column */}
          <div className="min-w-0 flex-1">
            <div className="h-[70vh] overflow-hidden rounded-lg border border-slate-200">
              <PageCanvas
                file={file}
                page={page}
                pageCount={numPages}
                onPageChange={setPage}
                zoom={zoom}
                onZoomChange={setZoom}
                size={size}
                onDocumentLoad={setNumPages}
                onPageLoad={(n, s) =>
                  setPageSizes((prev) => (prev[n] ? prev : { ...prev, [n]: s }))
                }
                onDeselect={() => setSelected(null)}
                toolbarExtra={
                  <>
                    <div className="mx-1 h-5 w-px bg-slate-200" />
                    <Select
                      value={newType}
                      onChange={(e) => setNewType(e.target.value as InputType)}
                      className="w-auto"
                      aria-label="Field type to add"
                    >
                      {INPUT_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </Select>
                    <Button size="sm" onClick={addField} disabled={!size}>
                      <Plus size={15} className="mr-1" /> Add field
                    </Button>
                  </>
                }
              >
                {onPage.map((f) => (
                  <FieldBox
                    key={f.uid}
                    field={f}
                    zoom={zoom}
                    selected={selected === f.uid}
                    onSelect={() => setSelected(f.uid)}
                    onChange={(geometry) => update(f.uid, geometry)}
                  >
                    {/* Render exactly as the field will stamp on the signed PDF
                        (checkbox → ink-exact square, date → platform format,
                        signature/initials → typed look) so boxes line up 1:1. */}
                    <FieldPreview
                      field={f}
                      zoom={zoom}
                      style={DEFAULT_TEXT_STYLE}
                      datePattern={datePattern}
                      roleLabel={f.signerRole || 'Signature'}
                    />
                  </FieldBox>
                ))}
              </PageCanvas>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Boxes are clamped to the page. Rotated pages (<code>/Rotate</code>) report pre-rotation
              dimensions — same as the signing engine.
            </p>
          </div>

          {/* Side panel: per-field controls + JSON */}
          <div className="lg:w-80 lg:shrink-0">
            <div className="mb-4 rounded-xl border border-slate-200 bg-white">
              <div className="border-b border-slate-100 px-4 py-2.5 text-sm font-semibold">
                Fields ({fields.length})
              </div>
              {fields.length === 0 ? (
                <p className="px-4 py-6 text-center text-sm text-slate-400">
                  No fields yet — pick a type and click <strong>Add field</strong>.
                </p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {fields.map((f) => (
                    <li
                      key={f.uid}
                      className={`cursor-pointer px-4 py-3 ${selected === f.uid ? 'bg-brand-50/60' : 'hover:bg-slate-50'}`}
                      onClick={() => {
                        setSelected(f.uid);
                        setPage(f.page);
                      }}
                    >
                      <div className="mb-2 flex items-center justify-between gap-2">
                        <span className="text-xs font-medium uppercase tracking-wide text-slate-500">
                          {f.type} · p{f.page}
                        </span>
                        <button
                          type="button"
                          className="text-slate-400 hover:text-red-500"
                          aria-label={`Remove ${f.type} field`}
                          onClick={(e) => {
                            e.stopPropagation();
                            remove(f.uid);
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-2 gap-2" onClick={(e) => e.stopPropagation()}>
                        <Labeled label="Type">
                          <Select
                            value={f.type}
                            onChange={(e) => {
                              const type = e.target.value as InputType;
                              update(f.uid, {
                                type,
                                dateMode: type === 'date' ? 'auto' : undefined,
                                mustCheck: type === 'checkbox' ? f.required : undefined,
                              });
                            }}
                          >
                            {INPUT_TYPES.map((t) => (
                              <option key={t} value={t}>
                                {t}
                              </option>
                            ))}
                          </Select>
                        </Labeled>
                        <Labeled label="Signer role">
                          <TextInput
                            value={f.signerRole}
                            onChange={(e) => {
                              update(f.uid, { signerRole: e.target.value });
                              setLastRole(e.target.value);
                            }}
                          />
                        </Labeled>
                      </div>
                      <div className="mt-2" onClick={(e) => e.stopPropagation()}>
                        <Toggle
                          checked={f.required}
                          onChange={(v) =>
                            update(f.uid, {
                              required: v,
                              ...(f.type === 'checkbox' ? { mustCheck: v } : {}),
                            })
                          }
                          label="Required"
                        />
                      </div>
                      <p className="mt-1 font-mono text-[11px] text-slate-500">
                        x {round(f.x)} · y {round(f.y)} · w {round(f.w)} · h {round(f.h)}
                      </p>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Copyable JSON */}
            <div className="rounded-xl border border-slate-200 bg-white">
              <div className="flex items-center justify-between border-b border-slate-100 px-4 py-2.5">
                <span className="text-sm font-semibold">fields[] JSON</span>
                <Button variant="secondary" size="sm" onClick={copyJson} disabled={fields.length === 0}>
                  {copied ? <Check size={14} className="mr-1" /> : <Copy size={14} className="mr-1" />}
                  {copied ? 'Copied' : 'Copy'}
                </Button>
              </div>
              <pre className="max-h-80 overflow-auto px-4 py-3 text-[11px] leading-relaxed text-slate-700">
                {fields.length === 0 ? '[]' : fieldsJson}
              </pre>
            </div>
            <p className="mt-2 text-xs text-slate-400">
              Paste into a <code>documents[]</code> entry alongside your <code>uploadId</code> from
              <code> POST /v1/uploads</code>.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}
